import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR, formatIST } from "@/lib/format";
import { PageHeader } from "@/components/Shell";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Check, X } from "lucide-react";

export default function AdminDeposits() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [action, setAction] = useState(null); // {type, row}
  const [utr, setUtr] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/admin/deposits${q}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      if (action.type === "approve") await api.post(`/admin/deposits/${action.row.txn_id}/approve`, { utr });
      else await api.post(`/admin/deposits/${action.row.txn_id}/reject`, { reason });
      toast.success(action.type === "approve" ? "Deposit approved & credited" : "Deposit rejected");
      setAction(null); setUtr(""); setReason(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Deposit Requests" desc="Verify the UTR against your bank, then approve to credit the user." />
      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList className="bg-slate-900/60">
          {["pending", "approved", "rejected", "all"].map((s) => <TabsTrigger key={s} value={s} data-testid={`dep-filter-${s}`} className="capitalize">{s}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="vp-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-slate-400">Txn ID</TableHead><TableHead className="text-slate-400">User</TableHead>
                <TableHead className="text-slate-400">Amount</TableHead><TableHead className="text-slate-400">UTR</TableHead>
                <TableHead className="text-slate-400">Date (IST)</TableHead><TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!rows ? <TableRow><TableCell colSpan={7} className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400 mx-auto" /></TableCell></TableRow> :
                rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">No deposit requests.</TableCell></TableRow> :
                rows.map((t) => (
                  <TableRow key={t.id} className="border-white/5" data-testid={`deposit-row-${t.txn_id}`}>
                    <TableCell className="font-mono text-xs text-slate-400">{t.txn_id}</TableCell>
                    <TableCell><div className="font-medium text-sm">{t.user.name}</div><div className="text-xs text-slate-500">{t.user.email}</div></TableCell>
                    <TableCell className="font-mono text-emerald-300">{formatINR(t.amount)}</TableCell>
                    <TableCell className="font-mono text-xs">{t.utr || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono">{formatIST(t.created_at)}</TableCell>
                    <TableCell><StatusBadge status={t.status} /></TableCell>
                    <TableCell className="text-right">
                      {t.status === "pending" ? (
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" onClick={() => { setAction({ type: "approve", row: t }); setUtr(t.utr || ""); }} data-testid={`approve-dep-${t.txn_id}`} className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] h-8"><Check className="h-4 w-4" /></Button>
                          <Button size="sm" variant="outline" onClick={() => setAction({ type: "reject", row: t })} data-testid={`reject-dep-${t.txn_id}`} className="border-rose-500/30 text-rose-300 bg-transparent hover:bg-rose-500/10 h-8"><X className="h-4 w-4" /></Button>
                        </div>
                      ) : <span className="text-xs text-slate-500">{t.remark || "—"}</span>}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent className="bg-[#121a28] border-white/10">
          <DialogHeader><DialogTitle className="font-display">{action?.type === "approve" ? "Approve Deposit" : "Reject Deposit"}</DialogTitle></DialogHeader>
          {action && (
            <div className="space-y-3">
              <div className="text-sm text-slate-400">{action.row.user.name} · {formatINR(action.row.amount)}</div>
              {action.type === "approve" ? (
                <div><Label className="text-slate-300">Verified UTR / Reference</Label><Input value={utr} onChange={(e) => setUtr(e.target.value)} data-testid="approve-utr-input" placeholder="Enter UTR number" className="mt-1.5 bg-slate-900/60 border-slate-700" /></div>
              ) : (
                <div><Label className="text-slate-300">Rejection Reason</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input" placeholder="e.g. Invalid UTR / amount mismatch" className="mt-1.5 bg-slate-900/60 border-slate-700" /></div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)} className="border-slate-700 bg-transparent">Cancel</Button>
            <Button onClick={submit} disabled={busy || (action?.type === "approve" ? !utr : !reason)} data-testid="confirm-dep-action"
              className={action?.type === "approve" ? "bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold" : "bg-rose-500 hover:bg-rose-400 text-white font-semibold"}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : action?.type === "approve" ? "Approve & Credit" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
